<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRiskStore } from '@/stores/riskData'
import { useQuoteIllustration, type PriceItem } from '@/composables/useQuoteIllustration'
import { useMembershipDetails } from '@/composables/useMembershipDetails'
import { gCollectorStore } from '@/stores/gCollectorStore'
import { useQuoteProductConfig } from '@/components/Quote/composables/useQuoteProductConfig'
const { config: productConfig } = useQuoteProductConfig(computed(() => props.productId))
const tenantKey = computed(() => productConfig.value?.tenantKey.toLowerCase() ?? '')
const productKey = computed(() => productConfig.value?.productKey.toLocaleLowerCase() ?? '')

const gStore = gCollectorStore()
if (!gStore.initialized)
{
  gStore.initFromWindow()
}
const { tenant } = storeToRefs(gStore)


import DocumentListBox from '../DocumentListBox.vue'

import ConfirmationPremiumSummary from './ConfirmationPremiumSummary.vue'
import ConfirmationPaymentSummary from './ConfirmationPaymentSummary.vue'
import ConfirmationEndorsements from './ConfirmationEndorsements.vue'
import ConfirmationStatementOfPrice from './ConfirmationStatementOfPrice.vue'
import ConfirmationStatementOfFact from './ConfirmationStatementOfFact.vue'
import ConfirmationPleaseNote from './ConfirmationPleaseNote/index.vue'

defineOptions({ name: 'ConfirmationTab' })

const props = defineProps<{
  productId?: string
}>()

const store = useRiskStore()
const { productOutput, action, riskData, productId, coverDetails } = storeToRefs(store)

// Factory passes the authoritative productId as a prop; the store payload does
// not reliably carry it. Fall back to the store getter just in case.
const effectiveProductId = computed(() => props.productId ?? productId.value)

const emit = defineEmits<{
  (e: 'nav-back'): void
  (e: 'nav-next'): void
  (e: 'nav-to', stepId: string): void
}>()

const selectedScheme = computed(() => {
  return productOutput.value?.ReturnSchemes?.find((scheme: any) => scheme.IsSelected)
})

const { generate: generateIllustration, buildStatementOfFact, buildCoverItems, getConfirmationDocuments } = useQuoteIllustration()

const normaliseDocName = (value: string) => String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')
const ILLUSTRATION_MATCH = normaliseDocName('Quote Illustration.pdf')

// Tenant allowlist of documents to show on this step (empty = unconfigured, in
// which case we fall back to showing whatever the API returned, as before).
const confirmationDocConfig = ref<string[]>([])

watch(
  [selectedScheme, effectiveProductId],
  async () => {
    confirmationDocConfig.value = selectedScheme.value
      ? await getConfirmationDocuments(effectiveProductId.value ?? '')
      : []
  },
  { immediate: true }
)

const hasDocConfig = computed(() => confirmationDocConfig.value.length > 0)

// API documents filtered to the tenant allowlist and ordered by it, matched
// exactly on file name (doc.Key). The 'Quote Illustration.pdf' entry is the
// client-generated PDF, handled separately as an action item.
const visibleDocuments = computed(() => {
  const documents: any[] = productOutput.value?.Documents ?? []
  if (!hasDocConfig.value) return documents

  const allowedFileNames = confirmationDocConfig.value
    .map(normaliseDocName)
    .filter((name) => name && name !== ILLUSTRATION_MATCH)

  return documents
    .map((doc: any) => ({ doc, order: allowedFileNames.indexOf(normaliseDocName(doc.Key)) }))
    .filter((entry) => entry.order !== -1)
    .sort((a, b) => a.order - b.order)
    .map((entry) => entry.doc)
})

// Show the Quote Illustration when unconfigured (back-compat) or when the tenant
// allowlist includes it.
const showIllustration = computed(() =>
  !hasDocConfig.value ||
  confirmationDocConfig.value.some((entry) => normaliseDocName(entry) === ILLUSTRATION_MATCH)
)

// The "Statement Of Price - Cover Details" rows, from the same tenant logic as
// the Quote Illustration PDF: the product's curated cover rows where its schema
// exports buildCoverItems, otherwise the scheme's features. Either way the
// premium/fee rows and addons are left to the cards above. Async because the
// tenant schema is loaded dynamically, so this mirrors sofSections below.
const priceItems = ref<PriceItem[]>([])

watch(
  [selectedScheme, effectiveProductId],
  async () => {
    priceItems.value = selectedScheme.value
      ? await buildCoverItems(selectedScheme.value, effectiveProductId.value ?? '')
      : []
  },
  { immediate: true }
)

// The journey answers (Proposer's Details / Cover Details / Assumptions), built
// from the same tenant logic as the Quote Illustration PDF's Statement Of Fact.
const sofSections = ref<Array<{ title: string; stepId?: string; items: Array<{ label: string; value: string }> }>>([])

watch(
  [selectedScheme, riskData, effectiveProductId],
  async () => {
    if (!selectedScheme.value) {
      sofSections.value = []
      return
    }
    sofSections.value = await buildStatementOfFact(riskData.value, effectiveProductId.value ?? '')
  },
  { immediate: true }
)

const ILLUSTRATION_KEY = 'quote-illustration'

// The Quote Illustration is generated on the fly (no server URL), so it is
// offered as a document-list action item once a scheme has been selected.
const documentActionItems = computed(() =>
  selectedScheme.value && showIllustration.value
    ? [{ key: ILLUSTRATION_KEY, label: 'Quote Illustration.pdf' }]
    : []
)

const onGenerateDocument = async (key: string) => {
  if (key !== ILLUSTRATION_KEY || !selectedScheme.value) return
  await generateIllustration(selectedScheme.value, riskData.value, effectiveProductId.value ?? '')
}

// Monthly instalment details show for off-account brokers and for
// Ceta / Aggregator / MoneySuperMarket members; everyone else sees the
// annual-only note.
const { showMonthlyDetails } = useMembershipDetails()
const isMonthly = showMonthlyDetails

</script>

<template>
  <div class="mT-15">
    <div class="alert alert-secondary" v-if="tenantKey === 'ceta' && productKey === 'rent-guarantee'">
        <strong>Confirmation —</strong> By continuing you confirm that you have had an opportunity to read the policy wording along with any applicable endorsements and exclusions and accept the terms and conditions outlined in the Insurance Product Information Document and Terms of Business.
    </div>

    <div class="row mb-4" v-if="visibleDocuments.length > 0 || documentActionItems.length > 0">
      <div class="col-12">
        <DocumentListBox
          :documents="visibleDocuments"
          :action-items="documentActionItems"
          @generate="onGenerateDocument"
        />
      </div>
    </div>

    <template v-if="selectedScheme">
      <ConfirmationPremiumSummary
        :scheme="selectedScheme"
        :action="action"
        :tenant="tenant"
        :product-name="productOutput?.ProductName"
        :isMonthly="isMonthly"
        :productId="effectiveProductId"
      />

      <ConfirmationPaymentSummary
        v-if="isMonthly"
        :scheme="selectedScheme"
        :action="action"
        :isMonthly="isMonthly"
        :isYearly="!isMonthly"
      />

      <!-- Commenting out for now as this may get brought back in to handle the summary -->
      <!-- <ConfirmationCoverSummary
        :scheme="selectedScheme"
        :product-output="productOutput"
      /> -->

      <ConfirmationStatementOfPrice :items="priceItems" />

      <ConfirmationEndorsements
        :scheme="selectedScheme"
      />

      <ConfirmationStatementOfFact
        :sections="sofSections"
        @change-details="(stepId: string) => emit('nav-to', stepId)"
      />

      <ConfirmationPleaseNote
      :isMonthly="isMonthly"
      :isYearly="!isMonthly"
      :interest-amount="selectedScheme.PaymentPlan?.Interest"
      :payment-plan-apr="selectedScheme.PaymentPlan?.APR"
      :payment-plan-fee="selectedScheme.PaymentPlan?.Fee"
      :total-payable="selectedScheme.PaymentPlan?.TotalPayable"
      :product-id="effectiveProductId"
    />
    </template>

    <div class="d-flex justify-content-between">
        <button class="btn btn-outline-primary" @click="emit('nav-back')"><em class="far fa-chevron-left mR-5"></em> Back</button>
        <button class="btn btn-outline-primary" @click="emit('nav-next')">Next <em class="far fa-chevron-right mL-5"></em></button>
    </div>
  </div>
</template>
